// src/components/ImageUploadCard.jsx

import React, { useRef, useState, useEffect } from "react";
import { toast } from "react-toastify";

/* ───────────── ICONE SVG INLINE ───────────── */
const IconX = ({ className = "w-6 h-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconExpand = ({ className = "w-5 h-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 3 3 3 3 9" />
    <line x1="3" y1="3" x2="10" y2="10" />
    <polyline points="15 21 21 21 21 15" />
    <line x1="14" y1="14" x2="21" y2="21" />
  </svg>
);

const IconTrash = ({ className = "w-5 h-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m5 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

export default function ImageUploadCard({
  label,
  name,
  onFileSelect,      // callback(filesArray: File[])
  previewImages = [], // array di URL esistenti (server)
  onRemove,          // callback quando un’immagine esistente viene rimossa (idx, filename)
  disabled,          // se true, nessuna azione consentita
}) {
  const [localFiles, setLocalFiles] = useState([]);       // File selezionati in questa sessione
  const [localPreviews, setLocalPreviews] = useState([]); // URL.createObjectURL per i file locali
  const [fullscreenIdx, setFullscreenIdx] = useState(null);
  const fileInputRef = useRef(null);

  // Revoca tutti i blob quando il componente si smonta
  useEffect(() => {
    return () => {
      localPreviews.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  // Se cambiano le preview server, pulisco le anteprime locali
  useEffect(() => {
    if (previewImages.length > 0 && localPreviews.length > 0) {
      localPreviews.forEach((u) => URL.revokeObjectURL(u));
      setLocalPreviews([]);
      setLocalFiles([]);
      onFileSelect?.([]); // notifico al genitore che non ci sono più file locali
    }
  }, [previewImages]);

  // Gestione della selezione file: aggiunge solo quelli nuovi
  const handleFileChange = (e) => {
    if (disabled) return;
    const chosen = Array.from(e.target.files || []);
    if (chosen.length === 0) return;

    // rimuovo eventuali duplicati (stesso name, size, lastModified)
    const trulyNew = chosen.filter((file) =>
      !localFiles.some(
        (f) =>
          f.name === file.name &&
          f.size === file.size &&
          f.lastModified === file.lastModified
      )
    );
    if (trulyNew.length === 0) {
      toast.info("Hai già selezionato questi file.");
      e.target.value = null;
      return;
    }

    // genero URL solo per i file effettivamente nuovi
    const newPreviews = trulyNew.map((f) => URL.createObjectURL(f));
    setLocalPreviews((prev) => [...prev, ...newPreviews]);

    setLocalFiles((prevFiles) => {
      const updated = [...prevFiles, ...trulyNew];
      onFileSelect?.(updated); // notifico al genitore l'array aggiornato
      return updated;
    });

    e.target.value = null;
  };

  // Rimuove un’anteprima locale (prima del salvataggio)
  const removeLocal = (idx) => {
    if (disabled) return;
    URL.revokeObjectURL(localPreviews[idx]);
    const newPreviews = localPreviews.filter((_, i) => i !== idx);
    setLocalPreviews(newPreviews);

    const newFiles = localFiles.filter((_, i) => i !== idx);
    setLocalFiles(newFiles);
    onFileSelect?.(newFiles);
  };

  // Rimuove un’immagine già salvata sul server
  const removeExistingImage = (idx) => {
    if (disabled) return;
    const filename = previewImages[idx].split("/").pop();
    onRemove?.(idx, filename);
  };

  const imagesToShow = [
    ...previewImages.map((u) => ({ url: u, isExisting: true })),
    ...localPreviews.map((u) => ({ url: u, isExisting: false })),
  ];
  const hasImages = imagesToShow.length > 0;

  return (
    <div className="flex flex-col">
      <div className="bg-primary rounded-2xl border-2 border-secondary shadow min-h-[440px] relative overflow-hidden text-white flex-1">
        {!hasImages && (
          <div className="absolute top-4 left-4 z-10">
            <div className="text-lg font-semibold">{label}</div>
            <div className="text-sm text-gray-300">
              Carica una o più immagini
            </div>
          </div>
        )}

        {hasImages && (
          <div
            className="flex overflow-x-auto snap-x snap-mandatory w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            {imagesToShow.map((img, idx) => (
              <div
                key={idx}
                className="relative flex-shrink-0 w-full h-full snap-center"
              >
                <img
                  src={img.url}
                  alt={`preview-${idx}`}
                  className="object-cover w-full h-full select-none rounded-2xl"
                  draggable={false}
                />

                <button
                  type="button"
                  className="absolute top-2 left-2 w-8 h-8 bg-black bg-opacity-50 rounded-full flex items-center justify-center backdrop-blur-sm"
                  onClick={() => setFullscreenIdx(idx)}
                >
                  <IconExpand className="text-white w-5 h-5" />
                </button>

                {!disabled && (
                  <button
                    type="button"
                    className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center shadow-md"
                    onClick={() =>
                      img.isExisting
                        ? removeExistingImage(idx)
                        : removeLocal(idx - previewImages.length)
                    }
                  >
                    <IconTrash className="text-white w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          id={name}
          type="file"
          name={name}
          multiple
          accept="image/*"
          onChange={handleFileChange}
          disabled={disabled}
          className="hidden"
        />

        {!hasImages && !disabled && (
          <div
            className="absolute inset-0 z-0 cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          />
        )}
      </div>

      {!disabled && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            className="bg-secondary hover:bg-secondary/90 text-white px-4 py-2 rounded-xl shadow"
            onClick={() => fileInputRef.current?.click()}
          >
            Carica foto
          </button>
        </div>
      )}

      {fullscreenIdx !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95"
          onClick={() => setFullscreenIdx(null)}
        >
          <img
            src={imagesToShow[fullscreenIdx].url}
            alt="fullscreen"
            className="max-w-full max-h-full object-contain rounded-xl"
          />

          {!disabled && (
            <button
              type="button"
              className="absolute top-4 right-16 w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                const img = imagesToShow[fullscreenIdx];
                if (img.isExisting) {
                  removeExistingImage(fullscreenIdx);
                } else {
                  removeLocal(fullscreenIdx - previewImages.length);
                }
                setFullscreenIdx(null);
              }}
            >
              <IconTrash className="text-white w-6 h-6" />
            </button>
          )}

          <button
            type="button"
            className="absolute top-4 right-4 w-10 h-10 bg-black bg-opacity-60 rounded-full flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenIdx(null);
            }}
          >
            <IconX className="text-white w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}
